// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Marc Planeck
import * as React from "react";
import {
  defineMessages,
  FormattedMessage,
  IntlShape,
  MessageDescriptor,
  IntlContext,
} from "react-intl";

import { Site } from "states/sitestates";
import { IState } from "states/states";
import { isSet } from "shared/utils/utils";
import { AlarmState } from "shared/types/globtypes";

// unacknowledged alarm counts of the connected site from its live updated alarm list, as the
// site list counts are only loaded at login; undefined until the alarms are loaded
export function getConnectedSiteAlarmCounts(state: IState) {
  const isLoaded = isSet(state.scheduler.alarm.lastData);
  const alarms = state.data.alarms;
  return {
    connectedUnackAlarms:
      isLoaded ?
        alarms.filter(
          (curItem) =>
            curItem.state === AlarmState.Active || curItem.state === AlarmState.AckPending,
        ).length
      : undefined,
    connectedUnclearedAlarms:
      isLoaded ? alarms.filter((curItem) => curItem.state === AlarmState.Active).length : undefined,
  };
}

interface ISiteInfoTagsExtProps {
  site?: Site;
  // live data connection is down, only set for the connected site
  offline?: boolean;
  // shortened alarm text, the full one is shown as tooltip
  compact?: boolean;
}

const messages = defineMessages({
  msg_offline: {
    id: "Offline",
  },
  msg_alarmPending: {
    id: "alarm pending",
    defaultMessage: "{count} alarm pending",
  },
  msg_alarmsPending: {
    id: "alarms pending",
    defaultMessage: "{count} alarms pending",
  },
  msg_alarmActive: {
    id: "alarm active",
    defaultMessage: "{count} active alarm",
  },
  msg_alarmsActive: {
    id: "alarms active",
    defaultMessage: "{count} active alarms",
  },
  msg_alarmUnack: {
    id: "alarm unacknowledged",
    defaultMessage: "{count} alarm unacknowledged",
  },
  msg_alarmsUnack: {
    id: "alarms unacknowledged",
    defaultMessage: "{count} alarms unacknowledged",
  },
  msg_alarmTotalUnack: {
    id: "alarm unacknowledged total",
    defaultMessage: "{count} total alarm unacknowledged",
  },
  msg_alarmsTotalUnack: {
    id: "alarms unacknowledged total",
    defaultMessage: "{count} total alarms unacknowledged",
  },
});

type ShortValues = { active: number | string; pending: number | string };
const shortMessages = defineMessages<{
  msg_alarmShort: ShortValues;
  msg_alarmsShort: ShortValues;
}>({
  msg_alarmShort: {
    id: "alarm short",
    defaultMessage: "{active} alarm, {pending} pending",
  },
  msg_alarmsShort: {
    id: "alarms short",
    defaultMessage: "{active} alarms, {pending} pending",
  },
});

const formatCount = (count: number) => (count > 99 ? "99+" : count);

// formatted count label, undefined if there are none
function formatAlarms(
  intl: IntlShape,
  count: number | undefined,
  singular: MessageDescriptor,
  plural: MessageDescriptor,
) {
  return isSet(count) && count > 0 ?
      intl.formatMessage(count == 1 ? singular : plural, { count: formatCount(count) })
    : undefined;
}

// full description of the site's alarms, undefined if there are none
export function getAlarmDescription(intl: IntlShape, site: Site | undefined) {
  const active = formatAlarms(
    intl,
    site?.unclearedAlarms,
    messages.msg_alarmActive,
    messages.msg_alarmsActive,
  );
  // "total" only next to the active count
  const unack =
    isSet(active) ?
      formatAlarms(
        intl,
        site?.unackAlarms,
        messages.msg_alarmTotalUnack,
        messages.msg_alarmsTotalUnack,
      )
    : formatAlarms(intl, site?.unackAlarms, messages.msg_alarmUnack, messages.msg_alarmsUnack);
  return [active, unack].filter(isSet).join(", ") || undefined;
}

// shortened description, the pending count includes the active alarms
function getAlarmShortDescription(intl: IntlShape, site: Site | undefined) {
  const active = site?.unclearedAlarms ?? 0;
  const pending = site?.unackAlarms ?? 0;
  return active > 0 && pending > 0 ?
      intl.formatMessage(
        active == 1 ? shortMessages.msg_alarmShort : shortMessages.msg_alarmsShort,
        {
          active: formatCount(active),
          pending: formatCount(pending),
        },
      )
    : formatAlarms(intl, pending, messages.msg_alarmPending, messages.msg_alarmsPending);
}

class SiteInfoTagsComponent extends React.Component<ISiteInfoTagsExtProps> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const alarmDescription = getAlarmDescription(this.context, this.props.site);
    return (
      <div className={`tagField${this.props.offline ? " offline" : ""}`}>
        {this.props.offline === true && (
          <span className="offline">
            <FormattedMessage id={messages.msg_offline.id} />
          </span>
        )}
        {isSet(alarmDescription) && (
          <span title={alarmDescription}>
            {this.props.compact ?
              getAlarmShortDescription(this.context, this.props.site)
            : alarmDescription}
          </span>
        )}
      </div>
    );
  }
}

export default SiteInfoTagsComponent;
